import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AdminPageHeader,
  Button,
  ButtonStyles,
  DashboardPageHeaderMobile,
  Input,
  MLPVersion,
  TextArea,
  Theme,
  useBreakpoint,
} from '../../../../../ui/components';
import { getTranslatedMessage, LoggerService, PAGES, trackPage } from '../../../../../ui/utils';
import { useAdminToast } from '../../../../../ui/components/AdminToast/AdminToast';
import TeamService from '../../../../../sdk/services/teamService';
import { useAPICall } from '../../../../../hooks';
import type { ButtonTheme } from '../../../../../ui/components';
import { isValidEmail } from '../../../../../ui/utils/common';
import { TeamData } from '../../../../admin/teams/teamsListConfig';
import { initTeamForm } from '../../../../admin/teams';
import styles from './index.module.scss';

export interface AddParticipantProps {
  isAdmin?: boolean;
}

export const AddParticipantDashboard = ({ isAdmin = false }: AddParticipantProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiCall = useAPICall();
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;
  const { adminToast } = useAdminToast();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('');
  const [emailValid, setEmailValid] = useState(false);
  const [teamData, setTeamData] = useState<TeamData>(initTeamForm);
  const [email, setEmail] = useState('');

  const onSave = async () => {
    const payload = {
      campaignId: teamData.campaignId,
      teamId: teamData.teamId,
      teamName: teamData.teamName,
      email: email,
    };
    // try {
    const result = await apiCall(TeamService.teamInvite, payload);

    if (!result?.data?.result) {
      if (result.status !== 500) {
        adminToast({
          message: getTranslatedMessage(t, result?.data?.message),
          theme: Theme.ERROR,
        });
      }
    } else {
      //navigate(`d/campaigns/${teamData.campaignId}/teams/${teamId}`);
      navigate(-1);
      adminToast({
        message: isAdmin
          ? t('admin.addParticipant.message.inviteSentSuccess')
          : t('dashboard.my-team.add-participant.message.inviteSentSuccess'),
        theme: Theme.SUCCESS,
      });
    }
  };

  const buttonConfig = [
    {
      title: isAdmin
        ? t('admin.addParticipant.sendInvitation')
        : t('dashboard.my-team.add-participant.label.sendInvitation'),
      buttonStyle: ButtonStyles.SOLID,
      theme: Theme.GREEN as ButtonTheme,
      disabled: !emailValid,
      onClick: onSave,
    },
  ];

  const fetchTeamData = async () => {
    try {
      const result = await apiCall(TeamService.getTeamDetails, `${teamId}`);
      setTeamData(result?.data?.data ?? initTeamForm);
    } catch (error) {
      LoggerService.log('Failed to retrieve team detail', error);
    }
  };

  const handleInputChange = (email: string) => {
    setEmailValid(isValidEmail(email));
    setEmail(email);
  };

  useEffect(() => {
    fetchTeamData();

    const timeout = setTimeout(() => {
      trackPage(PAGES.addParticipant);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className={styles['add-participant-main-container']}>
      {isDesktopView ? (
        <AdminPageHeader
          title={isAdmin ? t('admin.addParticipant.title') : t('dashboard.my-team.add-participant.label.title')}
          button={buttonConfig}
          withBackButton
          onBackButtonClick={() => {
            navigate(-1);
          }}
        />
      ) : (
        <DashboardPageHeaderMobile
          title={isAdmin ? t('admin.addParticipant.title') : t('dashboard.my-team.add-participant.label.title')}
          withBackButton
          onBackButtonClick={() => {
            navigate(-1);
          }}
        />
      )}
      <div className={styles['add-participant-form-container']}>
        <div className={styles['input-container']}>
          <div className={styles['pin-dropdown']}>
            <Input
              name={'campaignName'}
              placeholder={
                isAdmin
                  ? t('admin.addParticipant.input.placeholder.campaign')
                  : t('dashboard.my-team.add-participant.input.placeholder.campaign')
              }
              value={teamData.campaignName}
              disabled
              mlpVersion={MLPVersion.ONE}
              label={
                isAdmin
                  ? t('admin.addParticipant.input.campaign')
                  : t('dashboard.my-team.add-participant.input.campaign')
              }
            />
          </div>
          <div className={styles['team-name-section']}>
            <Input
              name={'teamName'}
              placeholder={
                isAdmin
                  ? t('admin.addParticipant.input.placeholder.team')
                  : t('dashboard.my-team.add-participant.input.placeholder.team')
              }
              value={teamData.teamName}
              disabled
              mlpVersion={MLPVersion.ONE}
              label={isAdmin ? t('admin.addParticipant.input.team') : t('dashboard.my-team.add-participant.input.team')}
            />
          </div>
        </div>
        <div className={styles['input-container']}>
          <TextArea
            mlpVersion={MLPVersion.ONE}
            label={
              isAdmin
                ? t('admin.addParticipant.input.description')
                : t('dashboard.my-team.add-participant.input.description')
            }
            placeholder={
              isAdmin
                ? t('admin.addParticipant.input.placeholder.description')
                : t('dashboard.my-team.add-participant.input.placeholder.description')
            }
            value={teamData?.description}
            disabled
          />
        </div>
      </div>
      <hr className={styles['horizontal-line']} />

      <div className={styles['add-participant-form-container']}>
        <div>
          <Input
            name={'email'}
            placeholder={
              isAdmin
                ? t('admin.addParticipant.input.placeholder.email')
                : t('dashboard.my-team.add-participant.input.placeholder.email')
            }
            value={email}
            mlpVersion={MLPVersion.ONE}
            message={
              !emailValid
                ? isAdmin
                  ? t('admin.addParticipant.message.enterValidEmail')
                  : t('dashboard.my-team.add-participant.message.enterValidEmail')
                : ''
            }
            theme={!emailValid ? Theme.ERROR : Theme.BLACK}
            label={isAdmin ? t('admin.addParticipant.input.email') : t('dashboard.my-team.add-participant.input.email')}
            onChange={e => {
              handleInputChange(e.target.value);
            }}
          />
        </div>
      </div>

      {!isDesktopView ? (
        <div className={styles['mobile-button-container']}>
          <Button
            title={
              isAdmin
                ? t('admin.addParticipant.sendInvitation')
                : t('dashboard.my-team.add-participant.label.sendInvitation')
            }
            buttonStyle={ButtonStyles.SOLID}
            disabled={!emailValid}
            onClick={onSave}
          />
        </div>
      ) : null}
    </div>
  );
};
